// @flow
import React, {useState, useMemo, useEffect} from 'react';
import {useLocation, Link, Switch, Route} from 'react-router-dom';
import {
  makeStyles,
  IconButton,
  Typography,
  Grid,
  Divider,
  Card,
  CardActionArea,
  Snackbar,
} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {ArrowBack, Add, Delete, Edit} from '@material-ui/icons';
import {useHistory} from 'react-router-dom';
import {colors} from '../../../../assets/styles/colors';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import LogisticPromotionsNewNational from './LogisticsPromotionsNewNational';
import LogisticPromotionsNewInternational from './LogisticsPromotionsNewInternational';
import DeleteConfirmationModal from '../../../products/components/DeleteConfirmationModal/DeleteConfirmationModal';
import {
  useCRUDPromotion,
  useFetchPromotions,
  useGetAvailableShippingCountries,
  useGetCurrencyCountry,
  useResetState,
} from './utils';
import {useStoreCountry} from '../../../country/utils';

type SubRoute = {
  name: string,
  path: string,
  enabled: boolean,
  disabled?: boolean,
  component?: () => any,
  id?: string,
  hide?: boolean,
  id2?: string,
};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
    backgroundColor: 'white',
    borderBottom: '1px solid #dbdbe0',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingBlock: theme.spacing(2),
    paddingInline: theme.spacing(2),
    borderTop: '1px solid #dbdbe0',
    borderBottom: '1px solid #dbdbe0',
    borderLeft: '1px solid #dbdbe0',
    borderRight: '1px solid #dbdbe0',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  wrapEnable: {
    padding: theme.spacing(0, 2),
    paddingLeft: 0,
  },
  checkboxLabel: {
    fontSize: '14px',
    lineHeight: '22px',
    color: '#717190',
    margin: theme.spacing(3, 0),
  },
  zone: {
    marginBlock: theme.spacing(2),
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  cardRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    height: '100%',
    width: '100%',
    boxShadow: 'unset',
    margin: '0 auto',
    backgroundColor: '#F8F9FB',
  },
  cardActionArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '100%',
    borderRadius: '12px',
  },
  empty: {
    display: 'flex',
    columnGap: '0.5rem',
    minHeight: 120,
    height: '100%',
    width: '100%',
    border: '2px dashed #B8B8C7',
    opacity: 0.6,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 20,
    borderRadius: '12px',
    '&:hover': {
      opacity: 1,
    },
    transition: 'opacity 0.2s',
  },
  empty_blocked: {
    display: 'flex',
    columnGap: '0.5rem',
    minHeight: 120,
    height: '100%',
    width: '100%',
    border: '2px dashed #d7d7e450',
    opacity: 0.7,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 20,
    borderRadius: '12px',
  },
  emptyCircle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: colors.grey300,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    minHeight: 120,
    borderRadius: '8px',
    gap: theme.spacing(1),
    backgroundColor: colors.grey100,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  textHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  buttonsHeader: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  deleteButton: {
    color: colors.error,
  },
  editButton: {
    color: colors.grey600,
  },
  bold: {
    fontWeight: 600,
  },
  paragraph_blocked: {
    backgroundColor: colors.grey400,
    padding: theme.spacing(1),
    borderRadius: '8px',
    color: colors.grey900,
  },
  gridContainer: {
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(2),
    borderLeft: '1px solid #dbdbe0',
    borderRight: '1px solid #dbdbe0',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
}));

export default function LogisticPromotions() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const country = useStoreCountry();

  const [isNational, setIsNational] = useState(false);
  const [alertOpenError, setAlertOpenError] = useState(false);
  const [alertOpenSuccess, setAlertOpenSuccess] = useState(false);
  const [showModalDeleteRate, setShowModalDeleteRate] = useState(false);
  const {promotionMethod, fetchPromotions} = useFetchPromotions();
  const availableShippingCountries = useGetAvailableShippingCountries();
  const currency = useGetCurrencyCountry();
  const {resetState} = useResetState();
  const {
    isSaving,
    savingPromotionSuccess,
    savingPromotionError,
    deletePromotion,
  } = useCRUDPromotion();

  const isAvailableOtherCountries =
    availableShippingCountries &&
    availableShippingCountries.filter(
      countryI => countryI?.code !== country?.code
    ).length > 0;

  const handleRedirectUrl = (url: string) => {
    history.push(url);
  };

  const handleModalDeleteClose = () => {
    setShowModalDeleteRate(false);
  };

  const handleDeleteClick = () => {
    const {
      internationalShippingPromotion,
      nationalShippingPromotion,
    } = promotionMethod;

    let storeShippingMethodPromotionSetting = {};

    if (isNational) {
      if (internationalShippingPromotion) {
        const {national, type, ...rest} = internationalShippingPromotion;
        storeShippingMethodPromotionSetting = {
          internationalPromotion: {
            ...rest,
          },
        };
      }
      deletePromotion({storeShippingMethodPromotionSetting});
    } else {
      if (nationalShippingPromotion) {
        const {national, type, ...rest} = nationalShippingPromotion;
        storeShippingMethodPromotionSetting = {
          nationalPromotion: {
            ...rest,
          },
        };
      }
      deletePromotion({storeShippingMethodPromotionSetting});
    }

    setShowModalDeleteRate(false);
  };

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const subRoutes: {[key: string]: SubRoute} = useMemo(
    () => ({
      national: {
        name: 'Crear promoción nacional',
        path: '/configure/logistics/promotions/national/new',
        enabled: true,
        // enabled: isMethodEnabled('STANDARD'),
        component: LogisticPromotionsNewNational,
        id: 'promotion-national',
        id2: 'promotion-national-configuration',
      },
      international: {
        name: 'Crear promoción internacional',
        path: '/configure/logistics/promotions/international/new',
        // enabled: isMethodEnabled('FLAT_RATE'),
        enabled: true,
        component: LogisticPromotionsNewInternational,
        id: 'promotion-international',
        id2: 'promotion-international-configuration',
      },
    }),
    []
  );

  const subRoutesArray = Object.keys(subRoutes)
    .map(k => subRoutes[k])
    .filter((route: SubRoute) => !route.hide);

  const subRoute: ?SubRoute = subRoutesArray.find(
    (route: SubRoute) => location.pathname === route.path
  );

  useEffect(() => {
    if (savingPromotionSuccess) {
      setAlertOpenSuccess(true);
      resetState();
    }
  }, [resetState, savingPromotionSuccess]);

  useEffect(() => {
    if (savingPromotionError) {
      setAlertOpenError(true);
    }
  }, [savingPromotionError]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <Snackbar
        open={alertOpenSuccess}
        autoHideDuration={4000}
        onClose={() => setAlertOpenSuccess(false)}
      >
        <Alert severity="success" onClose={() => setAlertOpenSuccess(false)}>
          Configuración guardada correctamente
        </Alert>
      </Snackbar>
      <Snackbar
        open={alertOpenError}
        autoHideDuration={4000}
        onClose={() => setAlertOpenError(false)}
      >
        <Alert severity="error" onClose={() => setAlertOpenError(false)}>
          Hubo un error al guardar la configuración
        </Alert>
      </Snackbar>

      {subRoute && (
        <>
          <div className={classes.subRouteHeader}>
            <IconButton
              variant="contained"
              size="small"
              component={Link}
              to={`/configure/logistics/promotions${window.location.search}`}
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.subRouteTitle} id={subRoute.id2}>
              {subRoute.name}
            </Typography>
          </div>
          {/* <Divider className={classes.divider} /> */}
        </>
      )}

      <Switch>
        <Route exact path="/configure/logistics/promotions">
          <div className={classes.gridContainer}>
            <Grid container spacing={2}>
              {/* {subRoutesArray.map((route: SubRoute, idx: number) => ( */}
              <Grid item xs={12} sm={6} md={6} lg={6}>
                {promotionMethod &&
                promotionMethod.nationalShippingPromotion ? (
                  <Card variant="outlined" className={classes.card}>
                    <div className={classes.header}>
                      <div className={classes.textHeader}>
                        <Typography align="left" className={classes.bold}>
                          Promoción Nacional
                        </Typography>
                      </div>
                      <div className={classes.buttonsHeader}>
                        <CustomButton
                          className={classes.deleteButton}
                          variant="text"
                          startIcon={<Delete />}
                          onClick={() => {
                            setShowModalDeleteRate(true);
                            setIsNational(true);
                          }}
                        >
                          Eliminar
                        </CustomButton>
                        <Divider orientation="vertical" flexItem />
                        <CustomButton
                          className={classes.editButton}
                          variant="text"
                          startIcon={<Edit />}
                          onClick={() =>
                            handleRedirectUrl(
                              `/configure/logistics/promotions/national/edit`
                            )
                          }
                        >
                          Editar
                        </CustomButton>
                      </div>
                    </div>
                    <div className={classes.containerData}>
                      <Typography align="left" className={classes.textGrey}>
                        A partir de {currency.symbol}{' '}
                        {
                          promotionMethod.nationalShippingPromotion
                            .minOrderAmount
                        }
                      </Typography>
                      <Typography align="left" className={classes.textGrey}>
                        El costo de envío será {currency.symbol}{' '}
                        {promotionMethod.nationalShippingPromotion.price}
                      </Typography>
                    </div>
                  </Card>
                ) : (
                  <Card classes={{root: classes.cardRoot}}>
                    <CardActionArea
                      className={classes.cardActionArea}
                      onClick={() =>
                        handleRedirectUrl(
                          `/configure/logistics/promotions/national/new`
                        )
                      }
                    >
                      <div className={classes.empty}>
                        <div className={classes.emptyCircle}>
                          <Add />
                        </div>
                        <Typography className={classes.paragraph}>
                          Crear promoción nacional
                        </Typography>
                      </div>
                    </CardActionArea>
                  </Card>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                {promotionMethod &&
                promotionMethod.internationalShippingPromotion ? (
                  <Card variant="outlined" className={classes.card}>
                    <div className={classes.header}>
                      <div className={classes.textHeader}>
                        <Typography align="left" className={classes.bold}>
                          Promoción Internacional
                        </Typography>
                      </div>
                      <div className={classes.buttonsHeader}>
                        <CustomButton
                          className={classes.deleteButton}
                          variant="text"
                          startIcon={<Delete />}
                          onClick={() => {
                            setShowModalDeleteRate(true);
                            setIsNational(false);
                          }}
                        >
                          Eliminar
                        </CustomButton>
                        <Divider orientation="vertical" flexItem />
                        <CustomButton
                          className={classes.editButton}
                          variant="text"
                          startIcon={<Edit />}
                          onClick={() => {
                            handleRedirectUrl(
                              `/configure/logistics/promotions/international/edit`
                            );
                          }}
                        >
                          Editar
                        </CustomButton>
                      </div>
                    </div>
                    <div className={classes.containerData}>
                      <Typography align="left" className={classes.textGrey}>
                        A partir de {currency.symbol}{' '}
                        {
                          promotionMethod.internationalShippingPromotion
                            .minOrderAmount
                        }
                      </Typography>
                      <Typography align="left" className={classes.textGrey}>
                        El costo de envío será {currency.symbol}{' '}
                        {promotionMethod.internationalShippingPromotion.price}
                      </Typography>
                    </div>
                  </Card>
                ) : (
                  <Card classes={{root: classes.cardRoot}}>
                    {isAvailableOtherCountries ? (
                      <CardActionArea
                        className={classes.cardActionArea}
                        onClick={() =>
                          handleRedirectUrl(
                            `/configure/logistics/promotions/international/new`
                          )
                        }
                      >
                        <div className={classes.empty}>
                          <div className={classes.emptyCircle}>
                            <Add />
                          </div>
                          <Typography className={classes.paragraph}>
                            Crear promoción internacional
                          </Typography>
                        </div>
                      </CardActionArea>
                    ) : (
                      <div className={classes.empty_blocked}>
                        <Typography className={classes.paragraph_blocked}>
                          No tienes envío internacional activado
                        </Typography>
                      </div>
                    )}
                  </Card>
                )}
              </Grid>
              {/* ))} */}
            </Grid>
          </div>
        </Route>
        {subRoutesArray.map((route: SubRoute, idx: number) => (
          <Route key={idx} path={route.path} component={route.component} />
        ))}
        <Route exact path="/configure/logistics/promotions/national/edit">
          <div className={classes.subRouteHeader}>
            <IconButton
              variant="contained"
              size="small"
              component={Link}
              to={`/configure/logistics/promotions${window.location.search}`}
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.subRouteTitle}>
              Editar Promoción Nacional
            </Typography>
          </div>
          {/* <Divider className={classes.divider} /> */}
          <LogisticPromotionsNewNational />
        </Route>
        <Route exact path="/configure/logistics/promotions/international/edit">
          <div className={classes.subRouteHeader}>
            <IconButton
              variant="contained"
              size="small"
              component={Link}
              to={`/configure/logistics/promotions${window.location.search}`}
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.subRouteTitle}>
              Editar Promoción Internacional
            </Typography>
          </div>
          {/* <Divider className={classes.divider} /> */}
          <LogisticPromotionsNewInternational />
        </Route>
      </Switch>

      <DeleteConfirmationModal
        title="¿Seguro que quieres eliminar esta tarifa?"
        content="Si lo eliminas no lo podrás recuperar."
        open={showModalDeleteRate}
        handleClose={handleModalDeleteClose}
        handleDeleteClick={handleDeleteClick}
        disabled={isSaving}
      />
    </div>
  );
}
